// CSSType does not provide a type for custom properties, https://github.com/frenic/csstype/issues/63
export type CssCustomProperties = {
  [key: `--${string}`]: string
}

export const DEFAULT_THEME_COLORS: CssCustomProperties = {
  '--theme-color__brand-primary': 'rgba(54, 81, 201, 1)',
  '--theme-color--brand-darker': 'rgba(27, 40, 101, 1)',
  '--theme-color--brand-dark': 'rgba(41, 61, 153, 1)',
  '--theme-color--brand-light': 'rgba(102, 123, 214, 1)',
  '--theme-color--brand-lighter': 'rgba(154, 168, 228, 1)',
  '--theme-color--brand-lightest': 'rgba(207, 213, 242, 1)',
  '--theme-color--status-error': 'rgba(169, 15, 15, 1)',
  '--theme-color--status-error-lightest': 'rgba(254, 246, 246, 1)',
  '--theme-color--status-success': 'rgba(46, 125, 50, 1)',
  '--theme-color--focus': 'rgba(41, 114, 255, 1)',
  '--theme-color--neutral-10': 'rgba(26, 25, 25, 1)', // black...
  '--theme-color--neutral-20': 'rgba(49, 47, 45, 1)',
  '--theme-color--neutral-30': 'rgba(80, 76, 73, 1)',
  '--theme-color--neutral-40': 'rgba(106, 101, 98, 1)',
  '--theme-color--neutral-45': 'rgba(120, 114, 110, 1)',
  '--theme-color--neutral-50': 'rgba(133, 127, 122, 1)',
  '--theme-color--neutral-60': 'rgba(157, 152, 149, 1)',
  '--theme-color--neutral-70': 'rgba(181, 178, 176, 1)',
  '--theme-color--neutral-80': 'rgba(206, 204, 202, 1)',
  '--theme-color--neutral-90': 'rgba(230, 229, 229, 1)',
  '--theme-color--neutral-95': 'rgba(243, 242, 242, 1)',
  '--theme-color--neutral-100': 'rgba(255, 255, 255, 1)', // ...white
  '--theme-color--brand-paypal': 'rgba(237, 192, 58, 1)',
  '--theme-color--brand-venmo': 'rgba(0, 140, 255, 1)',
  '--theme-color--selected-border': 'rgba(66, 92, 205, 1)',
}

export const DEFAULT_FOCUS_STYLES: CssCustomProperties = {
  '--theme--focus-outline': `2px dotted ${DEFAULT_THEME_COLORS['--theme-color--focus']}`,
  '--theme--focus-outline-offset': '2px',
}

export const DEFAULT_BORDER_STYLES: CssCustomProperties = {
  '--theme--border-radius-sm': '4px',
  '--theme--border-radius-md': '8px',
  '--theme--border-radius-lg': '12px',
  '--theme--border-radius-xl': '16px',
  '--theme--border-radius-xxl': '20px',
  '--theme--border-input': `1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-50']}`,
  '--theme--border-input-hover': `1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-40']}`,
  '--theme--border-input-error': `1px solid ${DEFAULT_THEME_COLORS['--theme-color--status-error']}`,
}

export const DEFAULT_BOX_SHADOW = '0px 4px 8px rgba(28, 25, 23, 0.12)'

export const DEFAULT_FONT_STYLES: CssCustomProperties = {
  '--theme-font__font-family--general':
    'system-ui,-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif',
  '--theme-font__font-family--heading':
    'system-ui,-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif',
  '--theme-font__font-family--paragraph':
    'system-ui,-apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif',
  '--theme-font--title-page__font-size': '28px',
  '--theme-font--title-page__line-height': '32px',
  '--theme-font--title-page__font-weight': '700',
  '--theme-font--title-page-sm__font-size': '24px',
  '--theme-font--title-page-sm__line-height': '28px',
  '--theme-font--title-page-sm__font-weight': '700',
  '--theme-font--body-lg__font-size': '18px',
  '--theme-font--body-lg__line-height': '24px',
  '--theme-font--body-default__font-size': '16px',
  '--theme-font--body-default__line-height': '24px',
  '--theme-font--body-sm__font-size': '14px',
  '--theme-font--body-sm__line-height': '20px',
  '--theme-font--body-bold__font-weight': '700',
  '--theme-font--body-default__font-weight': '400',
  '--theme-font--caption__font-size': '12px',
  '--theme-font--caption__line-height': '16px',
  '--theme-font--caption__font-weight': '400',
}

export const getBodyFonts = (
  theme: CssCustomProperties,
  size: 'sm' | 'default' | 'lg' = 'default',
  bold = false,
) => {
  return {
    fontFamily: theme['--theme-font__font-family--general'],
    fontSize: theme[`--theme-font--body-${size}__font-size`],
    lineHeight: theme[`--theme-font--body-${size}__line-height`],
    fontWeight: bold
      ? theme['--theme-font--body-bold__font-weight']
      : theme['--theme-font--body-default__font-weight'],
  }
}

export const getLabelFonts = (
  theme: CssCustomProperties,
  size: 'sm' | 'default' | 'lg' = 'default',
  bold = false,
) => {
  return {
    //fontFamily: theme['--theme-font__font-family--general'],
    fontSize: theme[`--theme-font--body-${size}__font-size`],
    lineHeight: theme[`--theme-font--body-${size}__line-height`],
    fontWeight: bold
      ? theme['--theme-font--body-bold__font-weight']
      : theme['--theme-font--body-default__font-weight'],
  }
}

export const getTitleFonts = (theme: CssCustomProperties, size: 'sm' | null = null) => {
  return {
    fontFamily: theme['--theme-font__font-family--general'],
    fontSize:
      size === 'sm'
        ? theme[`--theme-font--title-page-sm__font-size`]
        : theme[`--theme-font--title-page__font-size`],
    lineHeight:
      size === 'sm'
        ? theme[`--theme-font--title-page-sm__line-height`]
        : theme[`--theme-font--title-page__line-height`],
    fontWeight:
      size === 'sm'
        ? theme[`--theme-font--title-page-sm__font-weight`]
        : theme[`--theme-font--title-page__font-weight`],
  }
}

export const getCaptionFonts = (theme: CssCustomProperties) => ({
  //fontFamily: theme['--theme-font__font-family--paragraph'],
  fontSize: theme[`--theme-font--caption__font-size`],
  lineHeight: theme[`--theme-font--caption__line-height`],
  fontWeight: theme[`--theme-font--caption__font-weight`],
})
